// /web/src/main.jsx
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { Auth0Provider } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'

// Global styles
import './styles/global.css'
import './styles/public.css'
import './styles/protected.css'

// Shared components
import Layout from './components/Layout.jsx'
import PublicLayout from './components/PublicLayout.jsx'
import ProtectedRoute from './components/ProtectedRoute.jsx'
import AppLayout from './components/AppLayout.jsx'
import TrialLockout from './components/TrialLockout.jsx'

// Pages
import LandingPage from './LandingPage.jsx'
import BenefitsPage from './pages/Benefits.jsx'
import HowToPage from './pages/HowTo.jsx'
import Dashboard from './pages/Dashboard.jsx'
import ProjectDetail from './pages/ProjectDetail.jsx'
import Blog from './pages/Blog.jsx'
import Pricing from './pages/Pricing.jsx'
import Contact from './pages/Contact.jsx'
import Privacy from './pages/Privacy.jsx'
import Terms from './pages/Terms.jsx'
import Billing from './pages/Billing.jsx'
import NotFound from './pages/NotFound.jsx'
import NewProject from './pages/projects/NewProject.jsx'
import EditProject from './pages/projects/EditProject.jsx'
import FAQ from './pages/FAQ.jsx'
import CookiePolicy from './pages/Cookies.jsx'

// Project sub-pages
import ProjectAreas from './pages/projects/Areas.jsx'
import CalculatorsHub from './pages/projects/CalculatorsHub.jsx'

// Calculators (global + per-project)
import WCCalculator from './pages/calculators/WC.jsx'
import StairCalculator from './pages/calculators/Stairs.jsx'
import RiserCalculator from './pages/calculators/Risers.jsx'
import PlantCalculator from './pages/calculators/Plant.jsx'
import UValueCalculator from './pages/calculators/UValues.jsx'
import CycleCalculator from './pages/calculators/Cycles.jsx'
import EOTCalculator from './pages/calculators/end-of-trip.jsx'
import PlanningCalculator from './pages/calculators/planning.jsx'

const MassingPage = React.lazy(() => import('./features/massing/MassingPage'))

// New lifts app (reads project id from route or ?project=)
import LiftsApp from './components/Lifts_App.jsx'

// ---- Auth0 env (Vite) ----
const domain   = import.meta.env.VITE_AUTH0_DOMAIN
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
const audience = import.meta.env.VITE_AUTH0_AUDIENCE
const scope    = 'openid profile email read:projects write:projects run:calculations read:usage'

if (!domain || !clientId) {
  console.warn('Auth0 missing domain/clientId; check your web/.env')
}

// ---- Sentry init (errors first; tracing/profiling optional) ----
// If you prefer to hardcode a DSN temporarily for debugging, replace with the literal DSN string.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

Sentry.init({
  dsn: SENTRY_DSN,
  // This mirrors your suggested config: allows automatic IP address collection etc.
  sendDefaultPii: true,

  // Keep these integrations so you still get route traces and the option to profile.
  integrations: [
    Sentry.browserTracingIntegration({
      tracePropagationTargets: [
        'localhost',
        /^https?:\/\/127\.0\.0\.1(?::\d+)?/,
        /^https:\/\/api\.pebbledash\.co/,
      ],
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration(),
  ],

  // Sampling (tune in env without code changes)
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES ?? 0.2),
  profilesSampleRate: Number(import.meta.env.VITE_SENTRY_PROFILES ?? 0.2),

  environment: import.meta.env.MODE,                 // 'development' | 'production'
  release: import.meta.env.VITE_SENTRY_RELEASE,      // optional (set in CI)
})

// Optional: tiny fallback if something explodes high up
function Fallback() {
  return (
    <div style={{ padding: 24 }}>
      <h3>Something went wrong.</h3>
      <p>We’ve been notified and are looking into it.</p>
    </div>
  )
}



ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<Fallback />}>
      <Auth0Provider
        domain={domain}
        clientId={clientId}
        authorizationParams={{
          redirect_uri: window.location.origin, 
          audience,
          scope,
        }}
        cacheLocation="localstorage"
      >
        <BrowserRouter>
          <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route index element={<LandingPage />} />
              <Route path="benefits" element={<BenefitsPage />} />
              <Route path="how-to" element={<HowToPage />} />
            </Route>

            <Route element={<Layout />}>
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="*" element={<NotFound />} />
            </Route>

            <Route element={<ProtectedRoute><TrialLockout><AppLayout /></TrialLockout></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/projects/new" element={<NewProject />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/projects/:id/edit" element={<EditProject />} />
              <Route path="/projects/:id/areas" element={<ProjectAreas />} />
              <Route path="/projects/:id/calculators" element={<CalculatorsHub />} />
              <Route path="/projects/:id/massing" element={<React.Suspense fallback={<div style={{ padding: 24 }}>Loading 3D Massing…</div>}><MassingPage /></React.Suspense>} />
              <Route path="/projects/:id/calculators/lifts" element={<LiftsApp />} />
              <Route path="/projects/:id/calculators/wc" element={<WCCalculator />} />
              <Route path="/projects/:id/calculators/stairs" element={<StairCalculator />} />
              <Route path="/projects/:id/calculators/risers" element={<RiserCalculator />} />
              <Route path="/projects/:id/calculators/u-values" element={<UValueCalculator />} />

              <Route path="/calculators/wc" element={<WCCalculator />} />
              <Route path="/calculators/lifts" element={<LiftsApp />} />
              <Route path="/calculators/stairs" element={<StairCalculator />} />
              <Route path="/calculators/risers" element={<RiserCalculator />} />
              <Route path="/calculators/plant" element={<PlantCalculator />} />
              <Route path="/calculators/u-values" element={<UValueCalculator />} />
              <Route path="/calculators/cycles" element={<CycleCalculator />} />
              <Route path="/calculators/end-of-trip" element={<EOTCalculator />} />
              <Route path="/calculators/planning" element={<PlanningCalculator />} />

              <Route path="/billing" element={<Billing />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </Auth0Provider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
)

